import { ReactNode } from "react";
import Icon from "./Icon";
import type { Tier } from "@/lib/store";

const TIER_COLOR: Record<Tier, { bg: string; border: string; text: string; chip: string }> = {
  Seedling:    { bg: "var(--mnf-fiberBg)",            border: "var(--mnf-fiberBorder)",   text: "#2C6248",            chip: "rgba(168,212,184,0.95)" },
  Rooted:      { bg: "rgba(30,74,53,0.10)",           border: "rgba(30,74,53,0.30)",      text: "var(--mnf-f)",       chip: "var(--mnf-f)" },
  Flourishing: { bg: "rgba(160,120,32,0.10)",         border: "rgba(160,120,32,0.35)",    text: "var(--mnf-gold)",    chip: "var(--mnf-gold)" },
};

/** Tiny pill: "🔒 Rooted+" */
export function LockBadge({
  tier,
  size = "sm",
  className = "",
}: {
  tier: Tier;
  size?: "sm" | "md";
  className?: string;
}) {
  const c = TIER_COLOR[tier];
  const fontSize = size === "sm" ? 9.5 : 10.5;
  const padding = size === "sm" ? "3px 8px" : "4px 10px";
  return (
    <span
      className={`inline-flex items-center gap-1 mnf-body ${className}`}
      style={{
        padding, borderRadius: 999,
        background: c.bg, color: c.text,
        border: `1px solid ${c.border}`,
        fontSize, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
      }}
    >
      <Icon name="lock" size={size === "sm" ? 10 : 11} />
      {tier}+
    </span>
  );
}

/**
 * Subtle dim-and-lock overlay around any locked card. The wrapped child renders
 * with reduced opacity, a lock badge in the top-right, and a tap target that
 * shows an upsell sheet (handled by parent via `onTap`).
 */
export function LockOverlay({
  tier,
  onTap,
  children,
  testid,
}: {
  tier: Tier;
  onTap: () => void;
  children: ReactNode;
  testid?: string;
}) {
  return (
    <button
      onClick={onTap}
      data-testid={testid}
      className="relative w-full text-left transition-all hover-elevate"
      style={{ padding: 0, background: "transparent", border: "none", cursor: "pointer", borderRadius: 22 }}
    >
      <div style={{ opacity: 0.6, filter: "saturate(0.85)", pointerEvents: "none" }}>
        {children}
      </div>
      <span
        className="absolute"
        style={{ top: 12, right: 12, zIndex: 2 }}
      >
        <LockBadge tier={tier} />
      </span>
    </button>
  );
}

/**
 * Full upsell card — used in place of a locked feature when we want the
 * upsell to be the primary visual (e.g. "Direct line to Coach" in Flourish).
 */
export function UpsellCard({
  tier,
  eyebrow,
  title,
  description,
  ctaLabel = "See tiers",
  onCta,
  icon = "lock",
}: {
  tier: Tier;
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel?: string;
  onCta?: () => void;
  icon?: string;
}) {
  const c = TIER_COLOR[tier];
  return (
    <div
      className="mnf-organic relative overflow-hidden"
      style={{
        padding: 18,
        background: `linear-gradient(135deg, ${c.bg} 0%, rgba(245,240,232,0.65) 100%)`,
        border: `1px solid ${c.border}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 44, height: 44,
            borderRadius: "16px 6px 16px 6px",
            background: c.chip,
            color: tier === "Seedling" ? "#2C6248" : "#fff",
          }}
        >
          <Icon name={icon} size={20} weight={500} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="mnf-eyebrow" style={{ color: c.text }}>{eyebrow}</p>
          <p
            className="mnf-serif"
            style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3, marginTop: 3 }}
          >
            {title}
          </p>
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 4 }}>
            {description}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={onCta}
              data-testid={`button-upsell-${tier.toLowerCase()}`}
              className="mnf-body transition-all hover-elevate"
              style={{
                padding: "8px 14px", borderRadius: 999,
                background: c.chip, color: tier === "Seedling" ? "#1E4A35" : "#fff",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                border: "none", cursor: "pointer",
              }}
            >
              <Icon name="auto_awesome" size={13} /> {ctaLabel}
            </button>
            <LockBadge tier={tier} size="md" />
          </div>
        </div>
      </div>
    </div>
  );
}
